/**
 * What a HOST of the OEM web components knows, and nothing more.
 *
 * String literals rather than imports: the element arrives at runtime from `load.js`, so there
 * is no module in this repo to import it from — and importing one would put a second copy of
 * the domain on the page, which is the whole thing loading it remotely avoids.
 *
 * NO ATTRIBUTE CONSTANTS. The element observes no attributes at all: its environment, its API
 * base URL and its build version are compiled into the bundle, and one bundle is published per
 * environment. A host cannot point it at the wrong environment because there is nothing to
 * point.
 */

/** Registered by the bundle behind `config.oemComplianceLoaderUrl`. */
export const SI_OEM_COMPLIANCE_TAG = "si-oem-compliance";

/** Registered by the bundle behind `config.oemScoringLoaderUrl`. */
export const SI_OEM_SCORING_TAG = "si-oem-scoring";

/** Registered by the bundle behind `config.seoGeoLoaderUrl`. */
export const SI_SEO_GEO_TAG = "si-seo-geo";

/**
 * Every element prefixes its events with its domain, because each `detail` carries its own view
 * union and a page hosting more than one routes by the name alone. `-navigate` asks the host to
 * change view (`{ view, params, replace }`); `-session-ended` says the service refused the
 * token, and ending the session is the host's move. All are `bubbles` + `composed`, so they
 * cross the shadow root.
 */
export const SI_COMPLIANCE_NAVIGATE_EVENT = "si-compliance-navigate";
export const SI_COMPLIANCE_SESSION_ENDED_EVENT = "si-compliance-session-ended";
export const SI_SCORING_NAVIGATE_EVENT = "si-scoring-navigate";
export const SI_SCORING_SESSION_ENDED_EVENT = "si-scoring-session-ended";
export const SI_SEO_NAVIGATE_EVENT = "si-seo-navigate";
export const SI_SEO_SESSION_ENDED_EVENT = "si-seo-session-ended";

/** The `detail` of every `-navigate` event: one whole addressable state. `view` is the domain's
 * own view object; `si-seo-geo` derives it from `params`, so a host of that one reads only them. */
export interface OemNavigateDetail {
  view: unknown;
  params: URLSearchParams;
  replace: boolean;
}

/**
 * The view grammar the compliance and scoring bundles export beside their element, so a host
 * addresses a screen exactly as the element does. Paths are relative with no leading slash;
 * `viewForPath` answers `undefined` for a path that names no screen, including `""`.
 * `si-seo-geo` exports none: its view lives in the query string.
 */
export interface OemViewGrammar {
  pathForView: (
    view: unknown,
    query?: Readonly<Record<string, string>>
  ) => string;
  viewForPath: (path: string) => unknown;
}

export interface OemSessionUser {
  subject: string;
  displayName: string;
  roles: readonly string[];
  brandScopes: readonly string[];
}

/**
 * Handed to the element as a PROPERTY and never as an attribute, so the bearer token is never
 * serialized into the DOM, `outerHTML` or a snapshot. Optional: with none set, the element
 * reads Dealer Center's `JWTAuth` cookie itself.
 */
export interface OemSession {
  token: string;
  user: OemSessionUser;
}
